import React, { useEffect, useMemo, useRef, useState } from 'react';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import {
  TRIGGER_EXAMPLES,
  TASK_EXAMPLES,
  DO_EXAMPLES,
  DONT_EXAMPLES,
  ADHERENCE_LEVELS,
  topicBodyVariants,
  listSavedReplyTemplates,
} from './agentTemplates';
import './AgentEditor.css';

/**
 * AgentEditor — the guided form for one Inbox Reply case.
 *
 * Creating and editing run through this one component. Previously both flows
 * had their own copy of the same ~340 lines of markup, which is how they drifted
 * apart; there is now a single definition of what a case looks like.
 *
 * The form is split into three steps because the old single-scroll layout showed
 * tag colours and Calendly guidelines before the user had even said what the
 * case reacts to. Each step answers one question: which emails does it cover,
 * what does it do, and what else should happen.
 *
 * It is a screen, not a dialog. Setting up a case takes minutes of typing, and
 * a stray click on a backdrop or an idle Escape press threw all of it away. The
 * pane takes over the settings area instead, so leaving is a deliberate act and
 * the unsaved draft can be guarded by the caller.
 */

const PREDEFINED_COLORS = [
  '#1e40af', '#1d4ed8', '#7c3aed', '#6d28d9',
  '#be185d', '#9f1239', '#facc15', '#fbbf24',
  '#f59e0b', '#eab308', '#dc2626', '#ea580c',
  '#84cc16', '#22c55e', '#4f46e5', '#8b5cf6',
  '#ec4899', '#f43f5e',
];

const AVAILABLE_ICONS = [
  { name: 'calendar', path: '/img/tag_calendar.svg' },
  { name: 'close', path: '/img/tag_close.svg' },
  { name: 'coin', path: '/img/tag_coin.svg' },
  { name: 'eye', path: '/img/tag_eye.svg' },
  { name: 'rotate', path: '/img/tag_rotate.svg' },
  { name: 'time', path: '/img/tag_time.svg' },
  { name: 'zoom', path: '/img/tag_zoom.svg' },
];

const STEPS = [
  {
    id: 'case',
    label: 'Case',
    title: 'When is this case used?',
    sub: 'Describe the emails it covers. Every incoming reply is matched against that description.',
  },
  {
    id: 'answer',
    label: 'Answer',
    title: 'What should happen with them?',
    sub: 'Nothing leaves your inbox on its own. You always see it first.',
  },
  {
    id: 'rules',
    label: 'Rules',
    title: 'What must always and never happen?',
    sub: 'Optional, but this is where you stop the answers going off script.',
  },
  {
    id: 'details',
    label: 'Details',
    title: 'What else should this case do?',
    sub: 'Optional extras. Turn on what you want, leave the rest off.',
  },
];

const REPLY_MODES = [
  {
    id: 'instructions',
    icon: 'fa-wand-magic-sparkles',
    title: 'Describe it',
    text: 'The AI writes it fresh for every email.',
  },
  {
    id: 'template',
    icon: 'fa-file-lines',
    title: 'Write it yourself',
    text: 'Your text, adapted only as far as you allow.',
  },
  {
    id: 'saved',
    icon: 'fa-book-bookmark',
    title: 'Choose template',
    text: 'Pick one of your saved reply templates.',
  },
];

const ACTIONS = [
  {
    type: 'Reply',
    icon: 'fa-pen-nib',
    title: 'Write a reply',
    text: 'Ready in your inbox for a final look.',
  },
  {
    type: 'No Reply',
    icon: 'fa-circle-minus',
    title: 'Stay silent',
    text: 'No answer at all.',
  },
  {
    type: 'Only Tag',
    icon: 'fa-tag',
    title: 'Only label it',
    text: 'A label, nothing else.',
  },
];

/**
 * What still stops a given step from being finished — one function of the draft
 * rather than a check tied to the step you happen to be on, so the editor can
 * tell at any moment which steps are done and let you move between them.
 */
const blockerForStep = (stepId, draft, takenNames, userTopics = {}) => {
  if (stepId === 'case') {
    if (!draft.name.trim()) return 'Give the case a name.';
    // Two cases with the same name are indistinguishable everywhere they show
    // up later — the list, the inbox label, the logs.
    if (takenNames.some((n) => n.toLowerCase() === draft.name.trim().toLowerCase())) {
      return 'Another case already has this name.';
    }
    if (!draft.case.trim()) return 'Describe which emails this case covers.';
    return '';
  }
  if (stepId === 'answer' && draft.type === 'Reply') {
    if (draft.replyMode === 'template' && !draft.replyTemplate.trim()) return 'Write the text of the reply.';
    if (draft.replyMode === 'saved') {
      if (!draft.savedTemplateId) return 'Pick one of your saved templates.';
      const topic = userTopics[draft.savedTemplateId];
      if (!topic) return 'This template is gone. Pick another one.';
      if (topicBodyVariants(topic).length === 0) return 'This template has no body text yet.';
      return '';
    }
    if (draft.replyMode !== 'template' && !draft.task.trim()) return 'Describe what the reply should say.';
    return '';
  }
  if (stepId === 'details' && (draft.type === 'Only Tag' || draft.useTag) && !draft.tag.trim()) {
    return 'Enter a label.';
  }
  return '';
};

// The analyzer scores every incoming reply from 1 (hostile) to 10 (enthusiastic)
// before any case is considered. The range below is a filter on that score, so
// it is named after the number the user will see in the inbox, not after a mood.
const isAnyRating = (min, max) => (min || 1) <= 1 && (max || 10) >= 10;

const describeRating = (min, max) => (isAnyRating(min, max)
  ? 'any rating'
  : `rating ${min || 1}–${max || 10}`);

// ─────────────────────────────────────────────────────────────────────────────

const PositivityRange = ({ min, max, onChange }) => {
  const any = isAnyRating(min, max);

  return (
    <div className="agx-mood">
      <div className="agx-mood-head">
        <span className="agx-label">Positivity rating</span>
        <span className="agx-mood-value">{any ? 'Any · 1–10' : `${min}–${max}`}</span>
      </div>
      <p className="agx-help">
        Every incoming reply is scored from 1 to 10 before any case is checked.
        This one is only considered inside the range you pick.
      </p>

      <div className="agx-mood-slider">
        <Slider
          range
          min={1}
          max={10}
          step={1}
          value={[min, max]}
          allowCross={false}
          onChange={([nextMin, nextMax]) => onChange(nextMin, nextMax)}
          styles={{
            track: { background: 'linear-gradient(90deg, #6897B7, #9fc7e8)', height: 6 },
            rail: { background: '#eaeef3', height: 6 },
            handle: {
              borderColor: '#6897B7',
              borderWidth: 2,
              height: 22,
              width: 22,
              marginTop: -8,
              backgroundColor: '#fff',
              boxShadow: '0 2px 8px rgba(104, 151, 183, 0.35)',
              opacity: 1,
            },
          }}
        />
      </div>

      <div className="agx-mood-scale">
        <span>1 · hostile</span>
        <span>5 · neutral</span>
        <span>10 · enthusiastic</span>
      </div>

      <button
        type="button"
        className={`agx-mood-any ${any ? 'on' : ''}`}
        onClick={() => onChange(1, 10)}
        disabled={any}
      >
        <i className={`fa ${any ? 'fa-check' : 'fa-arrows-left-right'}`} />
        {any ? 'Any rating. Positivity is ignored' : 'Use any rating'}
      </button>
    </div>
  );
};

/**
 * Examples, collapsed by default. They help on the first case and are noise on
 * every one after that, so they stay out of the way until asked for instead of
 * competing with the field they belong to.
 */
const Suggestions = ({ items, onPick, label = 'Examples' }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="agx-sugg">
      <button
        type="button"
        className={`agx-sugg-toggle ${open ? 'open' : ''}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <i className="fa fa-chevron-right" />
        {label}
      </button>

      {open && (
        <div className="agx-sugg-list">
          {items.map((item) => (
            <button key={item} type="button" className="agx-sugg-item" onClick={() => onPick(item)}>
              {item}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/** How far the AI may stray from a fixed template. */
const AdherencePicker = ({ value, onChange }) => {
  const active = ADHERENCE_LEVELS.find((l) => l.id === value) || ADHERENCE_LEVELS[2];

  return (
    <div className="agx-adh">
      <span className="agx-label">How much may the AI change your text?</span>
      <div className="agx-adh-seg" role="radiogroup" aria-label="Freedom">
        {ADHERENCE_LEVELS.map((level) => (
          <button
            key={level.id}
            type="button"
            role="radio"
            aria-checked={level.id === value}
            className={`agx-adh-opt ${level.id === value ? 'sel' : ''}`}
            onClick={() => onChange(level.id)}
          >
            {level.short}
          </button>
        ))}
      </div>
      <p className="agx-adh-desc">
        <strong>{active.label}</strong>. {active.description}
      </p>
    </div>
  );
};

/**
 * Lets the case reuse one of the user's own templates instead of a text typed
 * inline. Reads `userTopics` (already merged from settings/customTopics +
 * settings/selectedTopics in AgentsSettings) and stores the RTDB *key* — never
 * the display name — so renaming a template elsewhere doesn't silently orphan
 * the case.
 *
 * Each template can hold several body variants (topicSpecificNotes). Rather
 * than forcing the user to pick one and lose the rotation, the default is
 * "Auto — rotate", which mirrors how the outbound writer uses variants; the
 * user can still pin a single variant when they want a fixed answer.
 */
const SavedTemplatePicker = ({ draft, patch, userTopics }) => {
  const templates = listSavedReplyTemplates(userTopics);
  const selected = templates.find(([id]) => id === draft.savedTemplateId);
  const missingSelected = !!draft.savedTemplateId && !selected;
  const usableVariants = topicBodyVariants(selected?.[1]);

  if (templates.length === 0) {
    return (
      <div className="agx-savedTpl agx-savedTpl--empty">
        <i className="fa fa-book-bookmark" />
        <div>
          <strong>No templates yet.</strong>
          <p>Create one under Settings → Templates, then come back and pick it here.</p>
        </div>
      </div>
    );
  }

  const activeVariant = draft.savedTemplateVariantId
    ? usableVariants.find((v) => v.id === draft.savedTemplateVariantId)
    : null;

  // Preview snippet — first non-empty variant if in Auto, otherwise the picked one.
  const previewText = (activeVariant?.text || usableVariants[0]?.text || '').trim();

  return (
    <div className="agx-savedTpl">
      <div className="agx-field">
        <label className="agx-label" htmlFor="agx-savedTpl">Template</label>
        <select
          id="agx-savedTpl"
          value={draft.savedTemplateId || ''}
          onChange={(e) => patch({
            savedTemplateId: e.target.value,
            // Reset the variant when the template changes — a variant id from a
            // different template would silently fall back to "Auto" otherwise.
            savedTemplateVariantId: '',
          })}
        >
          <option value="">Choose a template…</option>
          {missingSelected && (
            <option value={draft.savedTemplateId}>(template removed)</option>
          )}
          {templates.map(([id, topic]) => (
            <option key={id} value={id}>{topic.topic}</option>
          ))}
        </select>
        <span className="agx-help">
          The reply uses this template’s text, adherence, language and formatting. It stays linked by ID, so renaming the template keeps it working.
        </span>
      </div>

      {missingSelected && (
        <div className="agx-savedTpl-warn">
          <i className="fa fa-triangle-exclamation" />
          <span>This template is gone. Pick another one, or the case cannot save.</span>
        </div>
      )}

      {selected && (
        usableVariants.length === 0 ? (
          <div className="agx-savedTpl-warn">
            <i className="fa fa-triangle-exclamation" />
            <span>
              This template has no body text yet. Add at least one version under Templates before using it here.
            </span>
          </div>
        ) : (
          <>
            <div className="agx-field">
              <span className="agx-label">
                Which version? {usableVariants.length > 1 && <span className="agx-optional">{usableVariants.length} available</span>}
              </span>
              <div className="agx-savedTpl-variants" role="radiogroup" aria-label="Template version">
                <button
                  type="button"
                  role="radio"
                  aria-checked={!draft.savedTemplateVariantId}
                  className={`agx-savedTpl-variant ${!draft.savedTemplateVariantId ? 'sel' : ''}`}
                  onClick={() => patch({ savedTemplateVariantId: '' })}
                >
                  <span className="agx-savedTpl-variant-title">
                    <i className="fa fa-shuffle" /> Auto, rotate
                  </span>
                  <span className="agx-savedTpl-variant-sub">
                    {usableVariants.length > 1
                      ? `A different version for each email (${usableVariants.length} in rotation).`
                      : 'The only version, used every time.'}
                  </span>
                </button>
                {usableVariants.map((variant, index) => (
                  <button
                    key={variant.id || index}
                    type="button"
                    role="radio"
                    aria-checked={draft.savedTemplateVariantId === variant.id}
                    className={`agx-savedTpl-variant ${draft.savedTemplateVariantId === variant.id ? 'sel' : ''}`}
                    onClick={() => patch({ savedTemplateVariantId: variant.id || '' })}
                    disabled={!variant.id}
                    title={!variant.id ? 'This version has no id yet. Resave the template to enable pinning.' : undefined}
                  >
                    <span className="agx-savedTpl-variant-title">Version {index + 1}</span>
                    <span className="agx-savedTpl-variant-sub agx-savedTpl-variant-snippet">
                      {(variant.text || '').trim().slice(0, 90)}{(variant.text || '').length > 90 ? '…' : ''}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {previewText && (
              <div className="agx-savedTpl-preview">
                <span className="agx-label">Preview</span>
                <pre className="agx-savedTpl-preview-body">{previewText}</pre>
              </div>
            )}
          </>
        )
      )}
    </div>
  );
};

/**
 * An editable list of absolute rules. Deliberately a list and not a free text
 * box: one rule per line is what makes them checkable, and it keeps them out of
 * the softer "what should the reply say" instructions, which the AI weighs
 * against the incoming email rather than obeying.
 */
const RuleList = ({ tone, label, hint, items, onChange, placeholder, examples }) => {
  const [value, setValue] = useState('');
  const [showExamples, setShowExamples] = useState(false);

  const add = (text) => {
    const clean = (text || '').trim();
    if (!clean || items.includes(clean)) return;
    onChange([...items, clean]);
    setValue('');
  };

  const unused = examples.filter((e) => !items.includes(e));

  return (
    <div className={`agx-rules ${tone}`}>
      <div className="agx-rules-head">
        <span className="agx-rules-icon">
          <i className={`fa ${tone === 'do' ? 'fa-check' : 'fa-ban'}`} />
        </span>
        <span className="agx-rules-headText">
          <span className="agx-label">{label}</span>
          <span className="agx-help">{hint}</span>
        </span>
      </div>

      {items.length > 0 && (
        <ul className="agx-rules-list">
          {items.map((item, index) => (
            <li key={item} className="agx-rules-item">
              <span className="agx-rules-text">{item}</span>
              <button
                type="button"
                className="agx-rules-remove"
                onClick={() => onChange(items.filter((_, i) => i !== index))}
                aria-label={`Remove rule: ${item}`}
              >
                <i className="fa fa-xmark" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="agx-rules-add">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); add(value); }
          }}
          placeholder={placeholder}
        />
        <button
          type="button"
          className="agx-rules-addBtn"
          onClick={() => add(value)}
          disabled={!value.trim()}
        >
          Add
        </button>
      </div>

      {unused.length > 0 && (
        <>
          <button
            type="button"
            className={`agx-sugg-toggle ${showExamples ? 'open' : ''}`}
            onClick={() => setShowExamples(!showExamples)}
            aria-expanded={showExamples}
          >
            <i className="fa fa-chevron-right" />
            Examples
          </button>
          {showExamples && (
            <div className="agx-rules-ex">
              {unused.map((example) => (
                <button key={example} type="button" className="agx-rules-exBtn" onClick={() => add(example)}>
                  {example}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const FeatureCard = ({ on, onToggle, icon, kicker, title, text, preview }) => (
  <button type="button" className={`agx-feat ${on ? 'on' : ''}`} onClick={onToggle}>
    <span className="agx-feat-top">
      <span className="agx-feat-icon"><i className={`fa ${icon}`} /></span>
      <span className={`agx-switch ${on ? 'on' : ''}`}><span className="agx-switch-knob" /></span>
    </span>
    <span className="agx-feat-kicker">{kicker}</span>
    <span className="agx-feat-title">{title}</span>
    <span className="agx-feat-text">{text}</span>
    {preview && <span className="agx-feat-demo">{preview}</span>}
  </button>
);

const TagDesigner = ({ draft, patch }) => (
    <div className="agx-sub">
      <div className="agx-field">
        <label className="agx-label" htmlFor="agx-tag">Label</label>
        <input
          id="agx-tag"
          type="text"
          value={draft.tag}
          onChange={(e) => patch({ tag: e.target.value })}
          placeholder="e.g. interested"
        />
      </div>

      <div className="agx-field">
        <label className="agx-label" htmlFor="agx-tagdesc">
          Note <span className="agx-optional">optional</span>
        </label>
        <input
          id="agx-tagdesc"
          type="text"
          value={draft.tagDescription}
          onChange={(e) => patch({ tagDescription: e.target.value })}
          placeholder="What does this label mean?"
        />
      </div>

      <div className="agx-field">
        <span className="agx-label">Appearance</span>
        <div className="agx-swatches">
          {PREDEFINED_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              aria-label={`Colour ${color}`}
              className={`agx-swatch ${draft.tagColor === color ? 'sel' : ''}`}
              style={{ backgroundColor: color }}
              onClick={() => patch({ tagColor: color })}
            />
          ))}
        </div>
        <div className="agx-icons">
          <button
            type="button"
            className={`agx-iconOpt ${!draft.tagIcon ? 'sel' : ''}`}
            onClick={() => patch({ tagIcon: '' })}
          >
            No icon
          </button>
          {AVAILABLE_ICONS.map((icon) => (
            <button
              key={icon.name}
              type="button"
              aria-label={icon.name}
              className={`agx-iconOpt ${draft.tagIcon === icon.path ? 'sel' : ''}`}
              onClick={() => patch({ tagIcon: icon.path })}
            >
              <img src={icon.path} alt="" />
            </button>
          ))}
        </div>
      </div>

      <div className="agx-tagPreview">
        <span className="agx-label">Preview</span>
        <span className="agx-tagChip" style={{ backgroundColor: draft.tagColor }}>
          {draft.tagIcon && <img src={draft.tagIcon} alt="" />}
          {draft.tag.trim() || 'your label'}
        </span>
      </div>
    </div>
);

const CalendlyPicker = ({ draft, patch, calendly }) => {
  const { eventTypes = [], loading, error, onLoad } = calendly || {};

  if (eventTypes.length === 0) {
    return (
      <div className="agx-sub">
        <button type="button" className="agx-secondary agx-selfStart" onClick={onLoad} disabled={loading}>
          {loading ? 'Loading meeting types…' : 'Load meeting types from Calendly'}
        </button>
        {error && <p className="agx-error">{error}</p>}
        <p className="agx-help">
          Needs a Calendly API key under Admin → Integrations → Calendly.
        </p>
      </div>
    );
  }

  return (
    <div className="agx-sub">
      <div className="agx-field">
        <div className="agx-subhead">
          <span className="agx-label">Meeting type</span>
          <button type="button" className="agx-link" onClick={onLoad} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        <div className="agx-events">
          {eventTypes.map((event) => (
            <button
              key={event.uuid}
              type="button"
              className={`agx-event ${draft.selectedEventType === event.uuid ? 'sel' : ''}`}
              onClick={() => patch({ selectedEventType: event.uuid })}
            >
              <span className="agx-event-name">{event.name}</span>
              <span className="agx-event-meta">{event.duration} min</span>
            </button>
          ))}
        </div>
      </div>

      <div className="agx-field">
        <label className="agx-label" htmlFor="agx-cal-guide">
          How should it offer times? <span className="agx-optional">optional</span>
        </label>
        <textarea
          id="agx-cal-guide"
          rows="2"
          value={draft.calendlyGuidelines}
          onChange={(e) => patch({ calendlyGuidelines: e.target.value })}
          placeholder="e.g. Offer three slots on different days, prefer mornings."
        />
      </div>
    </div>
  );
};

/**
 * The case as it will actually behave, filled in while the form is answered.
 *
 * It deliberately shows only what is really stored — the trigger, the tone
 * window, the literal reply text or the instructions, the rules — and never
 * invents a sample answer. A made-up reply would be the one thing here that
 * cannot be trusted, and trust is the whole point of a preview.
 */
const LivePreview = ({ draft, step, stepCount, userTopics }) => {
  const action = ACTIONS.find((a) => a.type === draft.type) || ACTIONS[0];
  const usesTemplate = draft.type === 'Reply' && draft.replyMode === 'template';
  const usesSaved = draft.type === 'Reply' && draft.replyMode === 'saved';
  const ruleCount = (draft.dos?.length || 0) + (draft.donts?.length || 0);
  const showsTag = (draft.type === 'Only Tag' || draft.useTag) && draft.tag.trim();

  // Preview text for the saved-template mode: resolve the picked template and
  // variant so the user sees the actual text that will go out, not just the name.
  // Adherence comes from the template itself — there is no extra slider here.
  let savedText = '';
  let savedName = '';
  let savedVariantCount = 0;
  let savedAdherence = 'exact';
  if (usesSaved && draft.savedTemplateId && userTopics) {
    const topic = userTopics[draft.savedTemplateId];
    if (topic) {
      savedName = topic.topic || '';
      savedAdherence = topic.aiAdherence || 'exact';
      const usable = topicBodyVariants(topic);
      savedVariantCount = usable.length;
      const pinned = draft.savedTemplateVariantId
        ? usable.find((v) => v.id === draft.savedTemplateVariantId)
        : null;
      savedText = (pinned?.text || usable[0]?.text || '').trim();
    }
  }

  const adherenceId = usesSaved ? savedAdherence : draft.adherence;
  const level = ADHERENCE_LEVELS.find((l) => l.id === adherenceId)
    || (adherenceId === 'creative'
      ? { id: 'creative', label: 'Creative rewrite' }
      : ADHERENCE_LEVELS[2]);

  const body = usesSaved ? savedText : (usesTemplate ? draft.replyTemplate : draft.task);
  const showsAsTemplate = usesTemplate || usesSaved;

  return (
    <div className="agx-prev">
      <div className="agx-prev-top">
        <span className="agx-prev-eyebrow">Your case</span>
        <span className="agx-prev-progress">{Math.min(step + 1, stepCount)}/{stepCount}</span>
      </div>

      <div className="agx-prev-card">
        <div className="agx-prev-head">
          <span className={`agx-prev-icon ${action.type === 'Reply' ? 'reply' : action.type === 'No Reply' ? 'silent' : 'tag'}`}>
            <i className={`fa ${action.icon}`} />
          </span>
          <span className="agx-prev-name">
            {draft.name.trim() || <em>Unnamed case</em>}
          </span>
        </div>

        <div className="agx-prev-block">
          <span className="agx-kw">When</span>
          <p className="agx-prev-text">
            {draft.case.trim() || <em>Describe the emails this case covers.</em>}
          </p>
          <div className="agx-prev-pills">
            <span className="agx-prev-pill">
              {describeRating(draft.happinessMin, draft.happinessMax)}
            </span>
            {showsTag && (
              <span className="agx-prev-pill tag" style={{ backgroundColor: draft.tagColor }}>
                {draft.tagIcon && <img src={draft.tagIcon} alt="" />}
                {draft.tag.trim()}
              </span>
            )}
          </div>
        </div>

        <div className="agx-prev-block">
          <span className="agx-kw">Then</span>

          {draft.type === 'Reply' ? (
            <>
              <p className="agx-prev-mode">
                {usesSaved
                  ? (draft.savedTemplateId
                    ? (savedAdherence === 'exact'
                      ? `Template “${savedName || '…'}”, sent word for word`
                      : `Template “${savedName || '…'}”, ${level.label.toLowerCase()}`)
                    : 'Pick one of your templates')
                  : usesTemplate
                    ? (draft.adherence === 'exact'
                      ? 'Your text, sent word for word'
                      : `Your text, ${level.label.toLowerCase()}`)
                    : 'Written from your instructions'}
                {usesSaved && draft.savedTemplateId && !draft.savedTemplateVariantId && savedVariantCount > 1 && (
                  <span className="agx-prev-mode-sub"> · rotates {savedVariantCount} versions</span>
                )}
              </p>
              <div className={`agx-prev-mail ${showsAsTemplate ? 'verbatim' : ''}`}>
                {body && body.trim()
                  ? body
                  : <em>
                    {usesSaved
                      ? (draft.savedTemplateId
                        ? 'This template has no body text yet.'
                        : 'Pick a template above.')
                      : usesTemplate
                        ? 'Write the reply text.'
                        : 'Describe what the reply should say.'}
                  </em>}
              </div>
            </>
          ) : (
            <p className="agx-prev-text">
              {draft.type === 'No Reply'
                ? 'Left unanswered. The email still arrives, so you can reply by hand.'
                : 'Only labelled. No answer is written.'}
            </p>
          )}

          <div className="agx-prev-pills">
            {ruleCount > 0 && (
              <span className="agx-prev-pill">
                <i className="fa fa-shield-halved" /> {ruleCount} {ruleCount === 1 ? 'rule' : 'rules'}
              </span>
            )}
            {draft.type !== 'Only Tag' && draft.useCalendly && (
              <span className="agx-prev-pill"><i className="fa fa-calendar-check" /> Calendly times</span>
            )}
          </div>
        </div>
      </div>

      <p className="agx-prev-foot">
        <i className="fa fa-lock" />
        Replies wait in your inbox. Nothing is sent automatically.
      </p>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

export default function AgentEditor({
  mode = 'create',
  draft,
  onChange,
  onCancel,
  onSave,
  saving = false,
  error = '',
  userTopics,
  calendly,
  takenNames = [],
}) {
  const [step, setStep] = useState(0);
  const rootRef = useRef(null);
  const nameRef = useRef(null);
  const patch = (changes) => onChange({ ...draft, ...changes });

  // The settings pane (.settingsContent) keeps whatever scrollTop the case list
  // had. scrollIntoView(block: start) plus the name field's autoFocus then
  // pulled the form up under the World header, so the back bar and steps were
  // clipped and you had to scroll to see the screen. Reset the pane to the top
  // instead, and focus without moving scroll.
  useEffect(() => {
    const pane = rootRef.current?.closest('.settingsContent');
    if (pane) pane.scrollTop = 0;
    else window.scrollTo(0, 0);
    if (step === 0) nameRef.current?.focus({ preventScroll: true });
  }, [step]);

  const appendTo = (field, sentence) => {
    const current = (draft[field] || '').trim();
    patch({ [field]: current ? `${current}\n${sentence}` : sentence });
  };

  const setType = (type) => patch({
    type,
    // "Only label" is meaningless without a label, and a case that only labels
    // must not keep a stale toggle when it turns back into a reply.
    useTag: type === 'Only Tag' ? true : (draft.type === 'Only Tag' ? false : draft.useTag),
  });

  // Absolute rules only shape a written answer, so the step is pointless for the
  // cases that never produce one.
  const steps = useMemo(
    () => (draft.type === 'Reply' ? STEPS : STEPS.filter((s) => s.id !== 'rules')),
    [draft.type],
  );
  const current = steps[Math.min(step, steps.length - 1)];

  useEffect(() => {
    if (step > steps.length - 1) setStep(steps.length - 1);
  }, [steps.length, step]);

  const stepBlockers = useMemo(
    () => steps.map((s) => blockerForStep(s.id, draft, takenNames, userTopics)),
    [steps, draft, takenNames, userTopics],
  );

  // What still blocks this step — doubles as the hint next to the disabled
  // button, so the user never has to guess why they can't continue.
  const blocker = stepBlockers[Math.min(step, steps.length - 1)];
  const openBlocker = stepBlockers.find(Boolean) || '';

  // Once everything before a step is answered, that step is fair game. This is
  // what makes editing bearable: changing one line no longer means clicking
  // Continue through the whole wizard to reach the save button.
  const canGoTo = (i) => i <= step || stepBlockers.slice(0, i).every((b) => !b);

  const isLast = step === steps.length - 1;
  const needsTag = draft.type === 'Only Tag';
  const isEdit = mode === 'edit';

  return (
    <section className="agx" ref={rootRef} aria-label={mode === 'edit' ? 'Edit case' : 'New case'}>
      <header className="agx-top">
        <button type="button" className="agx-back" onClick={onCancel} disabled={saving}>
          <i className="fa fa-arrow-left" />
          All cases
        </button>

        <nav className="agx-track" aria-label="Steps">
          {steps.map((s, i) => {
            // A step you have not reached yet is not "done" just because nothing
            // is missing from it — an existing case is the one exception, since
            // all of it was answered before.
            const isActive = i === step;
            const done = !isActive && !stepBlockers[i] && (isEdit || i < step);
            const todo = !isActive && !!stepBlockers[i] && canGoTo(i);
            return (
              <button
                key={s.id}
                type="button"
                className={`agx-trackStep ${isActive ? 'active' : ''} ${done ? 'done' : ''} ${todo ? 'todo' : ''}`}
                onClick={() => { if (canGoTo(i)) setStep(i); }}
                disabled={!canGoTo(i)}
                title={stepBlockers[i] || undefined}
                aria-current={isActive ? 'step' : undefined}
              >
                <span className="agx-trackIdx">{i + 1}</span>
                {s.label}
              </button>
            );
          })}
        </nav>
      </header>

      <div className="agx-cols">
        <div className="agx-main">
          <div className="agx-stepHead">
            <h3>{current.title}</h3>
            <p>{current.sub}</p>
          </div>

          {current.id === 'case' && (
            <>
              <div className="agx-field">
                <label className="agx-label" htmlFor="agx-name">Case name</label>
                <input
                  id="agx-name"
                  ref={nameRef}
                  type="text"
                  value={draft.name}
                  onChange={(e) => patch({ name: e.target.value })}
                  placeholder="e.g. Pricing question"
                />
                <span className="agx-help">Only for you. Never appears in an email.</span>
              </div>

              <div className="agx-field">
                <label className="agx-label" htmlFor="agx-case">Describe the emails</label>
                <textarea
                  id="agx-case"
                  rows="4"
                  value={draft.case}
                  onChange={(e) => patch({ case: e.target.value })}
                  placeholder="In your own words, e.g. “The sender asks what it costs or requests a quote.”"
                />
                <span className="agx-help">
                  Of all your cases, the one that fits an email best is the one that runs.
                </span>
                <Suggestions
                  items={TRIGGER_EXAMPLES}
                  onPick={(text) => patch({ case: text })}
                />
              </div>

              <PositivityRange
                min={draft.happinessMin}
                max={draft.happinessMax}
                onChange={(min, max) => patch({ happinessMin: min, happinessMax: max })}
              />
            </>
          )}

          {current.id === 'answer' && (
            <>
              <div className="agx-actions">
                {ACTIONS.map((action) => (
                  <button
                    key={action.type}
                    type="button"
                    className={`agx-action ${draft.type === action.type ? 'sel' : ''}`}
                    onClick={() => setType(action.type)}
                  >
                    <span className="agx-action-check"><i className="fa fa-check" /></span>
                    <span className="agx-action-icon"><i className={`fa ${action.icon}`} /></span>
                    <span className="agx-action-title">{action.title}</span>
                    <span className="agx-action-text">{action.text}</span>
                  </button>
                ))}
              </div>

              {draft.type === 'Reply' && (
                <>
                  <div className="agx-field">
                    <span className="agx-label">Where does the answer come from?</span>
                    <div className="agx-modes">
                      {REPLY_MODES.map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          className={`agx-mode ${draft.replyMode === m.id ? 'sel' : ''}`}
                          onClick={() => patch({ replyMode: m.id })}
                        >
                          <span className="agx-mode-icon"><i className={`fa ${m.icon}`} /></span>
                          <span className="agx-mode-text">
                            <span className="agx-mode-title">{m.title}</span>
                            <span className="agx-mode-sub">{m.text}</span>
                          </span>
                          <span className="agx-mode-check"><i className="fa fa-check" /></span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {draft.replyMode === 'template' && (
                    <>
                      <div className="agx-field">
                        <label className="agx-label" htmlFor="agx-template">Your reply text</label>
                        <textarea
                          id="agx-template"
                          rows="8"
                          value={draft.replyTemplate}
                          onChange={(e) => patch({ replyTemplate: e.target.value })}
                          placeholder={'Hi,\n\nthanks for getting back to me. Glad this is interesting for you.\n\nThe price depends on how much you want covered, so the quickest way is a short call…'}
                        />
                        <span className="agx-help">
                          Signature and footer come from your account settings.
                        </span>
                      </div>

                      <AdherencePicker
                        value={draft.adherence}
                        onChange={(adherence) => patch({ adherence })}
                      />
                    </>
                  )}

                  {draft.replyMode === 'saved' && (
                    <SavedTemplatePicker draft={draft} patch={patch} userTopics={userTopics} />
                  )}

                  {draft.replyMode !== 'template' && draft.replyMode !== 'saved' && (
                    <div className="agx-field">
                      <label className="agx-label" htmlFor="agx-task">What should the reply say?</label>
                      <textarea
                        id="agx-task"
                        rows="5"
                        value={draft.task}
                        onChange={(e) => patch({ task: e.target.value })}
                        placeholder="e.g. Thank them for the reply, answer the question in two sentences and propose a short call."
                      />
                      <span className="agx-help">
                        Instructions, not a template. Written fresh for every email.
                      </span>
                      <Suggestions
                        label="Guidelines"
                        items={TASK_EXAMPLES}
                        onPick={(text) => appendTo('task', text)}
                      />
                    </div>
                  )}
                </>
              )}

              {draft.type === 'No Reply' && (
                <div className="agx-note">
                  <i className="fa fa-circle-info" />
                  <span>
                    Nothing is written. The mail still appears in your inbox, so you can
                    answer by hand.
                  </span>
                </div>
              )}

              {draft.type === 'Only Tag' && (
                <div className="agx-note">
                  <i className="fa fa-circle-info" />
                  <span>Only a label, no answer. Set it up in the next step.</span>
                </div>
              )}
            </>
          )}

          {current.id === 'rules' && (
            <>
              <div className="agx-note">
                <i className="fa fa-shield-halved" />
                <span>Hard limits. They outrank everything else, in every email.</span>
              </div>

              <RuleList
                tone="do"
                label="Always"
                hint="Every reply must do this."
                items={draft.dos || []}
                onChange={(dos) => patch({ dos })}
                placeholder="Add something the reply must always do"
                examples={DO_EXAMPLES}
              />

              <RuleList
                tone="dont"
                label="Never"
                hint="No reply may ever do this."
                items={draft.donts || []}
                onChange={(donts) => patch({ donts })}
                placeholder="Add something the reply must never do"
                examples={DONT_EXAMPLES}
              />
            </>
          )}

          {current.id === 'details' && (
            <>
              {needsTag ? (
                <div className="agx-field">
                  <span className="agx-label">Label</span>
                  <span className="agx-help">Required. This case only labels.</span>
                  <TagDesigner draft={draft} patch={patch} />
                </div>
              ) : (
                <>
                  <div className="agx-feats">
                    <FeatureCard
                      on={draft.useTag}
                      onToggle={() => patch({ useTag: !draft.useTag })}
                      icon="fa-tags"
                      kicker="Inbox"
                      title="Tag matching emails"
                      text="Every match gets a coloured label. Filter the inbox by it later."
                      preview={<span className="agx-feat-chip" style={{ background: '#1e40af' }}>Interested</span>}
                    />
                    <FeatureCard
                      on={draft.useCalendly}
                      onToggle={() => patch({ useCalendly: !draft.useCalendly })}
                      icon="fa-calendar-check"
                      kicker="Reply"
                      title="Offer a meeting time"
                      text="The reply names real free slots from your Calendly. They pick one."
                      preview={(
                        <>
                          <span className="agx-feat-slot">Tue 10:00</span>
                          <span className="agx-feat-slot">Wed 14:30</span>
                        </>
                      )}
                    />
                  </div>
                  {draft.useTag && <TagDesigner draft={draft} patch={patch} />}
                  {draft.useCalendly && (
                    <CalendlyPicker draft={draft} patch={patch} calendly={calendly} />
                  )}
                </>
              )}
            </>
          )}
        </div>

        <aside className="agx-side">
          <LivePreview draft={draft} step={step} stepCount={steps.length} userTopics={userTopics} />
        </aside>
      </div>

      <footer className="agx-foot">
        <span className={`agx-foot-hint ${error ? 'err' : ''}`}>
          {error || blocker || openBlocker}
        </span>

        <div className="agx-foot-actions">
          <button
            type="button"
            className="agx-ghost"
            onClick={() => (step > 0 ? setStep(step - 1) : onCancel())}
            disabled={saving}
          >
            {step > 0 ? 'Back' : 'Cancel'}
          </button>

          {/* An existing case is already complete, so saving must not be parked
              behind the last step — you came here to change one thing. */}
          {isEdit ? (
            <>
              {!isLast && (
                <button
                  type="button"
                  className="agx-secondary"
                  onClick={() => setStep(step + 1)}
                  disabled={!!blocker || saving}
                >
                  Next
                </button>
              )}
              <button
                type="button"
                className="agx-primary"
                onClick={onSave}
                disabled={!!openBlocker || saving}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </>
          ) : isLast ? (
            <button
              type="button"
              className="agx-primary"
              onClick={onSave}
              disabled={!!openBlocker || saving}
            >
              {saving ? 'Saving…' : 'Create case'}
            </button>
          ) : (
            <button
              type="button"
              className="agx-primary"
              onClick={() => setStep(step + 1)}
              disabled={!!blocker}
            >
              Continue
            </button>
          )}
        </div>
      </footer>
    </section>
  );
}
